import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import {
  Breadcrumb,
  Loader,
  AdBlockerPopup,
  CommunitySlotsModal,
  Stepper,
} from 'components';
import { addMonths, formatSubscriptionDate } from 'utils/dates';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import {
  buildStripeCheckoutPresentmentBody,
  createInvoiceSubscriptionRequest,
  createStripeCheckoutSessionRequest,
  postChangePlanRequest,
} from 'services/subscriptions';
import { getLocation } from 'services/geolocation';
import {
  selectAllSubscriptions,
  selectCurrentUserSubscription,
  selectIsSubscriptionLoading,
} from 'store/selectors/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { getCommunities } from 'store/selectors/communities';
import {
  SubscriptionFrequency,
  SubscriptionPlan,
  SubscriptionStatus,
} from 'types';

import * as Ch from './styled';
import {
  InvoiceDocIcon,
  MastercardIcon,
  SepaIcon,
  StripeIcon,
  StripeWordmark,
  VisaIcon,
} from './PaymentBrandIcons';
import {
  isYupValidationError,
  validateInvoicePayerEmails,
} from './invoicePayerEmailsSchema';
import StripeCheckoutOutcome from 'pages/UserProfileSubscription/StripeCheckoutOutcome';
import { useCheckoutProration } from './useCheckoutProration';
import { OutsideAppPageContentWrapper } from 'components/Shared/styled';

type CheckoutMoneyProps = {
  ready: boolean;
  skeletonWidth?: string;
  skeletonHeight?: number;
  children?: React.ReactNode;
};

const CheckoutMoney = ({
  ready,
  skeletonWidth = '4.5rem',
  skeletonHeight = 14,
  children,
}: CheckoutMoneyProps) => {
  if (!ready) {
    return (
      <span
        className="skeleton skeleton-text"
        style={{
          display: 'inline-block',
          width: skeletonWidth,
          height: skeletonHeight,
          verticalAlign: 'middle',
        }}
        aria-hidden
      />
    );
  }
  return <>{children}</>;
};

const formatCheckoutAmount = (value: number | string) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return String(value);
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

type SubscriptionInnerProps = {
  planFallback?: SubscriptionPlan | null;
  initialActiveCommunityIds?: string[];
};

const SubscriptionInner = ({
  planFallback = null,
  initialActiveCommunityIds,
}: SubscriptionInnerProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subscriptionId: currentSubscriptionId } = useParams();
  const [searchParams] = useSearchParams();
  const subscriptions = useSelector(selectAllSubscriptions);
  const activeSubscription = useSelector(selectCurrentUserSubscription);
  const isSubscriptionLoading = useSelector(selectIsSubscriptionLoading);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const [hasAdBlocker, setHasAdBlocker] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<'direct' | 'invoice'>(
    'direct',
  );
  const [payerEmailRows, setPayerEmailRows] = useState<string[]>(['']);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [directCheckoutLoading, setDirectCheckoutLoading] = useState(false);
  const [directCheckoutError, setDirectCheckoutError] = useState<string | null>(
    null,
  );
  const [invoiceResult, setInvoiceResult] = useState<{
    hostedInvoiceUrl: string | null;
    amountLabel?: string | null;
  } | null>(null);
  const [communitySlotsOpen, setCommunitySlotsOpen] = useState(false);
  const [activeCommunityIds, setActiveCommunityIds] = useState<string[] | null>(
    initialActiveCommunityIds?.length ? initialActiveCommunityIds : null,
  );
  const [hasObservedSubscriptionLoad, setHasObservedSubscriptionLoad] =
    useState(false);

  const frequency = searchParams.get('frequency');

  const currentUserSubscriptionFrequency = activeSubscription?.frequency;
  const currentUserSubscriptionStatus = activeSubscription?.status;
  const currentUserSubscription = useMemo(() => {
    if (planFallback && planFallback.id === currentSubscriptionId) {
      return planFallback;
    }
    return subscriptions?.find(({ id }) => id === currentSubscriptionId);
  }, [subscriptions, currentSubscriptionId, planFallback]);

  const currentPlan = currentUserSubscription as SubscriptionPlan;
  const {
    maxGroups,
    maxMembers,
    maxCommunities,
    maxStorage,
    currency,
    monthlyValue,
    yearlyValue,
    name,
    productName,
    tax,
  } = currentPlan;

  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const changePlanPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;
  const displayPlanName = productName || name;

  const isCustom = name.includes('Enterprise');

  const isCurrent =
    currentUserSubscriptionStatus !== SubscriptionStatus.CANCELLED &&
    currentSubscriptionId === activeSubscription?.subscriptionPlanId &&
    currentUserSubscriptionFrequency === frequency;
  const currentPrice =
    frequency === SubscriptionFrequency.YEAR ? yearlyValue : monthlyValue;

  const displayPayButton = useMemo(() => {
    if (!isCurrent) {
      return true;
    }
    if (
      new Date().getTime() >
        new Date(activeSubscription?.endDate || null).getTime() ||
      (activeSubscription?.status === SubscriptionStatus.ACTIVE &&
        activeSubscription?.planId === SubscriptionStatus.MIGRATED)
    ) {
      return true;
    }
    return false;
  }, [isCurrent, activeSubscription]);

  const needsSubscriptionResolution =
    displayPayButton && !isCurrent && !isCustom;

  const isSubscriptionPending = useMemo(() => {
    if (!needsSubscriptionResolution) return false;
    if (activeSubscription) return isSubscriptionLoading;
    return isSubscriptionLoading || !hasObservedSubscriptionLoad;
  }, [
    needsSubscriptionResolution,
    activeSubscription,
    isSubscriptionLoading,
    hasObservedSubscriptionLoad,
  ]);

  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
  }, [dispatch]);

  useEffect(() => {
    if (isSubscriptionLoading) {
      setHasObservedSubscriptionLoad(true);
    }
  }, [isSubscriptionLoading]);

  const ownedCommunitiesList = useMemo(() => {
    if (!profile?.userId) return [];
    return communities
      .filter((c) => c.communityOwner?.userId === profile.userId)
      .map((c) => ({
        id: c.id,
        name: c.name ?? '',
        logo: c.logo ?? null,
        subscriptionSlotActive: c.subscriptionSlotActive,
      }));
  }, [communities, profile?.userId]);

  const activeCommunitiesForSlots =
    activeSubscription?.info?.activeCommunities ??
    activeSubscription?.info?.communities ??
    0;

  const needsCommunitySlotsChoice = useMemo(() => {
    const max = maxCommunities ?? 0;
    if (!profile?.userId || max <= 0) return false;
    return activeCommunitiesForSlots > max;
  }, [activeCommunitiesForSlots, maxCommunities, profile?.userId]);

  const pendingSlotSelection = activeCommunityIds;

  useEffect(() => {
    if (!needsCommunitySlotsChoice || !displayPayButton) return;
    if (pendingSlotSelection?.length) return;
    setCommunitySlotsOpen(true);
  }, [needsCommunitySlotsChoice, displayPayButton, pendingSlotSelection]);

  const expirationDate = useMemo(() => {
    if (displayPayButton) {
      const now = new Date();
      const isOnTrial =
        activeSubscription?.planId === SubscriptionStatus.TRIAL ||
        activeSubscription?.status === SubscriptionStatus.TRIAL;

      if (isOnTrial && activeSubscription?.endDate) {
        const trialEnd = new Date(activeSubscription.endDate);
        if (trialEnd.getTime() > now.getTime()) {
          return trialEnd;
        }
      }

      return now;
    }

    return new Date(activeSubscription.endDate);
  }, [displayPayButton, activeSubscription]);

  const yearlySaveVsMonthly = useMemo(
    () => monthlyValue * 12 - yearlyValue,
    [monthlyValue, yearlyValue],
  );

  const {
    isUpgradeCheckout,
    proration,
    pricesReady,
    isCheckoutDetailsLoading,
    fromPlanName,
    showProrationCredit,
    showRemainingCredit,
    renewalDateFromPreview,
    planChangeDirection,
  } = useCheckoutProration({
    displayPayButton,
    checkoutPlanId: currentSubscriptionId,
    activeSubscription,
    frequency,
    currentPrice,
    isSubscriptionPending,
  });

  const displayCurrency =
    isUpgradeCheckout && proration?.currency ? proration.currency : currency;

  const currencySym =
    displayCurrency === 'EUR' ? '€' : displayCurrency === 'CHF' ? 'CHF ' : '$';

  const renewalDateStr = useMemo(() => {
    if (isUpgradeCheckout && renewalDateFromPreview) {
      return formatSubscriptionDate(
        new Date(renewalDateFromPreview),
        i18n.language,
      );
    }
    const renewal: Date =
      frequency === SubscriptionFrequency.MONTH
        ? displayPayButton
          ? addMonths(expirationDate, 1)
          : expirationDate
        : displayPayButton
        ? addMonths(expirationDate, 12)
        : expirationDate;
    return formatSubscriptionDate(renewal, i18n.language);
  }, [
    frequency,
    displayPayButton,
    expirationDate,
    i18n.language,
    isUpgradeCheckout,
    renewalDateFromPreview,
  ]);

  const linePriceForDisplay =
    isUpgradeCheckout && proration ? proration.planLinePrice : currentPrice;

  const displayedLinePrice = useMemo(() => {
    if (tax > 0) {
      return formatCheckoutAmount((linePriceForDisplay * (100 - tax)) / 100);
    }
    return formatCheckoutAmount(linePriceForDisplay);
  }, [linePriceForDisplay, tax]);

  const planLineSubtext = useMemo(() => {
    const days = proration?.daysRemaining;
    if (!isUpgradeCheckout || days == null || days <= 0) {
      return null;
    }

    const price = formatCheckoutAmount(proration.planLinePrice);

    if (frequency === SubscriptionFrequency.YEAR) {
      return t('subscriptions.checkout.planLineSubtextAnnual', {
        currency: currencySym,
        price,
        days,
      });
    }

    return t('subscriptions.checkout.planLineSubtextMonthly', {
      currency: currencySym,
      price,
      days,
    });
  }, [
    currencySym,
    frequency,
    isUpgradeCheckout,
    proration?.daysRemaining,
    proration?.planLinePrice,
    t,
  ]);

  const previousPlanLineSubtext = useMemo(() => {
    const days = proration?.daysRemaining;
    if (!showProrationCredit || days == null || days <= 0) {
      return null;
    }

    return t('subscriptions.checkout.daysRemainingSubtext', { days });
  }, [proration?.daysRemaining, showProrationCredit, t]);

  const dueToday = useMemo(() => {
    const amount =
      isUpgradeCheckout && proration ? proration.totalDueToday : currentPrice;
    return Math.max(0, amount);
  }, [isUpgradeCheckout, proration, currentPrice]);

  const formatCreditAmount = useCallback(
    (amount: number) => `−${currencySym}${formatCheckoutAmount(amount)}`,
    [currencySym],
  );

  const formatRemainingCreditAmount = useCallback(
    (amount: number) => `${currencySym}${formatCheckoutAmount(amount)}`,
    [currencySym],
  );

  const approxMonthlyEq = useMemo(() => {
    if (frequency !== SubscriptionFrequency.YEAR || !yearlyValue) return null;
    return Math.round((yearlyValue / 12) * 100) / 100;
  }, [frequency, yearlyValue]);

  const planEmoji = isCustom ? '🏢' : '🚀';

  const includedItems = useMemo(
    () =>
      (
        [
          'subscriptions.checkout.included1',
          'subscriptions.checkout.included2',
          'subscriptions.checkout.included3',
          'subscriptions.checkout.included4',
          'subscriptions.checkout.included5',
          'subscriptions.checkout.included6',
          'subscriptions.checkout.included7',
          'subscriptions.checkout.included8',
        ] as const
      ).map((key) => t(key)),
    [t],
  );

  const showInvoiceForm =
    displayPayButton && frequency === SubscriptionFrequency.YEAR;

  const updatePayerRow = useCallback((index: number, value: string) => {
    setPayerEmailRows((rows) => rows.map((r, j) => (j === index ? value : r)));
  }, []);

  const addPayerRow = useCallback(() => {
    setPayerEmailRows((rows) => [...rows, '']);
  }, []);

  const removePayerRow = useCallback((index: number) => {
    setPayerEmailRows((rows) =>
      rows.length <= 1 ? rows : rows.filter((_, j) => j !== index),
    );
  }, []);

  const submitInvoiceSubscription = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setInvoiceError(null);
      if (!currentSubscriptionId) {
        setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
        return;
      }
      let payerEmails: string[];
      try {
        payerEmails = validateInvoicePayerEmails(payerEmailRows, t);
      } catch (err) {
        if (isYupValidationError(err)) {
          setInvoiceError(err.errors[0] ?? err.message);
          return;
        }
        setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
        return;
      }
      if (needsCommunitySlotsChoice && !pendingSlotSelection?.length) {
        setCommunitySlotsOpen(true);
        return;
      }
      setInvoiceSubmitting(true);
      try {
        let geo: { country_code2?: string } | null = null;
        try {
          geo = await getLocation();
        } catch {
          geo = { country_code2: 'CH' };
        }
        const data = await createInvoiceSubscriptionRequest({
          subscriptionPlanId: currentSubscriptionId,
          payerEmails,
          ...buildStripeCheckoutPresentmentBody(geo),
          ...(pendingSlotSelection?.length
            ? { activeCommunityIds: pendingSlotSelection }
            : {}),
        });
        setInvoiceResult({
          hostedInvoiceUrl: data.hostedInvoiceUrl,
          amountLabel: data.amountLabel,
        });
        dispatch(getCurrentUserSubscription.trigger());
      } catch {
        setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
      } finally {
        setInvoiceSubmitting(false);
      }
    },
    [
      currentSubscriptionId,
      payerEmailRows,
      profile?.email,
      dispatch,
      needsCommunitySlotsChoice,
      pendingSlotSelection,
      t,
    ],
  );

  const startStripeCheckout = useCallback(async (): Promise<void | false> => {
    setDirectCheckoutError(null);

    if (needsCommunitySlotsChoice && !pendingSlotSelection?.length) {
      setCommunitySlotsOpen(true);
      return false;
    }
    if (!currentSubscriptionId) {
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
      return;
    }
    const freq =
      frequency === SubscriptionFrequency.YEAR ||
      frequency === SubscriptionFrequency.MONTH
        ? frequency
        : null;

    if (!freq) {
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
      return;
    }

    setDirectCheckoutLoading(true);
    try {
      if (isUpgradeCheckout) {
        const data = await postChangePlanRequest({
          subscriptionPlanId: currentSubscriptionId,
          frequency: freq,
          paymentMethod: 'card',
          ...(pendingSlotSelection?.length
            ? { activeCommunityIds: pendingSlotSelection }
            : {}),
        });

        if (data?.checkoutUrl) {
          window.location.replace(data.checkoutUrl);
          return;
        }

        window.location.replace(
          `${window.location.origin}${subscriptionPath}?stripe_success=1`,
        );

        return;
      }

      let geo: { country_code2?: string } | null = null;
      try {
        geo = await getLocation();
      } catch {
        geo = { country_code2: 'CH' };
      }

      const data = await createStripeCheckoutSessionRequest({
        subscriptionPlanId: currentSubscriptionId,
        frequency: freq,
        ...buildStripeCheckoutPresentmentBody(geo),
      });

      if (data?.url) {
        window.location.replace(data.url);
        return;
      }

      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
    } catch (err) {
      const apiError = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      if (apiError === 'COMMUNITY_SLOTS_EXCEED_PLAN') {
        setDirectCheckoutError(null);
        setCommunitySlotsOpen(true);
        return false;
      }
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
    } finally {
      setDirectCheckoutLoading(false);
    }
  }, [
    currentSubscriptionId,
    frequency,
    isUpgradeCheckout,
    needsCommunitySlotsChoice,
    pendingSlotSelection,
    subscriptionPath,
    t,
  ]);

  const onCommunitySlotsClose = useCallback(() => {
    setCommunitySlotsOpen(false);
  }, []);

  const onCommunitySlotsSavedCheckout = useCallback(
    async (selectedIds: string[]) => {
      setActiveCommunityIds(selectedIds);
    },
    [],
  );

  const payActionsBlock = useMemo(() => {
    if (!displayPayButton || paymentMethod !== 'direct') return null;
    const amountLabel = pricesReady
      ? `${currencySym}${formatCheckoutAmount(dueToday)}`
      : '';
    return (
      <Ch.PayActions>
        {directCheckoutError ? (
          <Ch.InvoiceFormError role="alert">
            {directCheckoutError}
          </Ch.InvoiceFormError>
        ) : null}
        <Ch.BtnPurchase
          type="button"
          disabled={directCheckoutLoading || !pricesReady}
          onClick={() => {
            void startStripeCheckout();
          }}
        >
          <Ch.BtnPurchaseInner>
            {directCheckoutLoading ? (
              <Loader type="button" size="28px" thickness={2} />
            ) : !pricesReady ? (
              <CheckoutMoney ready={false} skeletonWidth="10rem">
                {null}
              </CheckoutMoney>
            ) : (
              <>
                <span aria-hidden>🔒</span>
                {t('subscriptions.checkout.completePurchase', {
                  amount: amountLabel,
                })}
              </>
            )}
          </Ch.BtnPurchaseInner>
        </Ch.BtnPurchase>
      </Ch.PayActions>
    );
  }, [
    displayPayButton,
    paymentMethod,
    currencySym,
    dueToday,
    pricesReady,
    directCheckoutLoading,
    directCheckoutError,
    startStripeCheckout,
    t,
  ]);

  if (invoiceResult) {
    return (
      <OutsideAppPageContentWrapper minHeight="100svh">
        <StripeCheckoutOutcome
          variant="success"
          checkoutFlow="invoice"
          planLabel={displayPlanName}
          hostedInvoiceUrl={invoiceResult.hostedInvoiceUrl}
          amountLabel={invoiceResult.amountLabel}
          onDismiss={() => navigate(subscriptionPath, { replace: true })}
        />
      </OutsideAppPageContentWrapper>
    );
  }

  const CheckoutPageWrapComponent = isCustom
    ? Ch.EnterpriseCheckoutPageWrap
    : Ch.CheckoutPageWrap;
  const CheckoutGridComponent = isCustom
    ? Ch.EnterpriseCheckoutLayoutGrid
    : Ch.CheckoutGrid;

  return (
    <OutsideAppPageContentWrapper minHeight="100svh">
      <AdBlockerPopup
        isOpen={hasAdBlocker}
        onClose={() => {
          setHasAdBlocker(false);
        }}
      />
      <Ch.CheckoutBackdrop>
        <CheckoutPageWrapComponent>
          <Breadcrumb
            items={[
              {
                label: t('changePlan.breadcrumbSubscription'),
                to: subscriptionPath,
              },
              {
                label: t('subscriptions.checkout.breadcrumbCheckout', {
                  plan: displayPlanName,
                }),
              },
            ]}
          />

          <Stepper
            steps={[
              {
                label: t('subscriptions.checkout.stepPlanSelected'),
                status: 'done',
                to: changePlanPath,
              },
              {
                label: t('subscriptions.checkout.stepOrderSummary'),
                status: 'active',
              },
              {
                label: t('subscriptions.checkout.stepConfirmation'),
                status: 'pending',
              },
            ]}
          />

          <CheckoutGridComponent>
            <div>
              <Ch.OrderCard>
                <Ch.OrderCardTitle>
                  {t('subscriptions.orderSummary')}
                </Ch.OrderCardTitle>
                <Ch.PlanSummaryRow>
                  <Ch.PlanIcon aria-hidden>{planEmoji}</Ch.PlanIcon>
                  <div>
                    <Ch.PlanInfoName>{displayPlanName}</Ch.PlanInfoName>
                    <Ch.PlanInfoSub>
                      {t('subscriptions.checkout.planInfoSub', {
                        billing:
                          frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.yearly')
                            : t('subscriptions.monthly'),
                        count: maxCommunities,
                        teams: t('changePlan.specCommunities'),
                      })}
                    </Ch.PlanInfoSub>
                    {isCustom ? (
                      <Ch.PlanBadge $variant="enterprise">
                        {t('changePlan.tierEnterprise')}
                      </Ch.PlanBadge>
                    ) : isCurrent ? (
                      <Ch.PlanBadge $variant="current">
                        {t('changePlan.currentPlanBadge')}
                      </Ch.PlanBadge>
                    ) : isCheckoutDetailsLoading ? (
                      <Ch.PlanBadgeSkeleton
                        className="skeleton skeleton-text"
                        aria-hidden
                      />
                    ) : isUpgradeCheckout &&
                      planChangeDirection === 'downgrade' ? (
                      <Ch.PlanBadge $variant="downgrade">
                        {t('subscriptions.checkout.badgeDowngrade', {
                          plan: fromPlanName,
                        })}
                      </Ch.PlanBadge>
                    ) : isUpgradeCheckout ? (
                      <Ch.PlanBadge $variant="upgrade">
                        {t('subscriptions.checkout.badgeUpgrade', {
                          plan: fromPlanName,
                        })}
                      </Ch.PlanBadge>
                    ) : (
                      <Ch.PlanBadge $variant="new">
                        {t('subscriptions.checkout.badgeNew')}
                      </Ch.PlanBadge>
                    )}
                  </div>
                </Ch.PlanSummaryRow>

                <Ch.SpecPills>
                  <Ch.SpecPill>
                    <span aria-hidden>🏠</span>
                    <span>
                      <Ch.SpecPillVal>{maxCommunities}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specCommunities')}
                    </span>
                  </Ch.SpecPill>
                  <Ch.SpecPill>
                    <span aria-hidden>👥</span>
                    <span>
                      {t('changePlan.upTo')}{' '}
                      <Ch.SpecPillVal>{maxMembers}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specMembers')}
                    </span>
                  </Ch.SpecPill>
                  {/* <Ch.SpecPill>
                    <span aria-hidden>📋</span>
                    <span>
                      <Ch.SpecPillVal>{maxGroups}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specSchedulers')}
                    </span>
                  </Ch.SpecPill> */}
                  <Ch.SpecPill>
                    <span aria-hidden>💾</span>
                    <span>
                      <Ch.SpecPillVal>{maxStorage} GB</Ch.SpecPillVal>{' '}
                      {t('changePlan.specStorage')}
                    </span>
                  </Ch.SpecPill>
                </Ch.SpecPills>

                <Ch.PriceBreakdown>
                  <Ch.PbRow>
                    <Ch.PbLabelCol>
                      <Ch.PbLabel>
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('subscriptions.checkout.planLineAnnual', {
                              plan: displayPlanName,
                            })
                          : t('subscriptions.checkout.planLineMonthly', {
                              plan: displayPlanName,
                            })}
                      </Ch.PbLabel>
                      {planLineSubtext ? (
                        <Ch.PbSubtext>{planLineSubtext}</Ch.PbSubtext>
                      ) : null}
                    </Ch.PbLabelCol>
                    <Ch.PbVal>
                      <CheckoutMoney ready={pricesReady}>
                        {currencySym}
                        {proration?.chargeAmount ?? displayedLinePrice}
                      </CheckoutMoney>
                    </Ch.PbVal>
                  </Ch.PbRow>
                  {showProrationCredit ? (
                    <Ch.PbRow>
                      <Ch.PbLabelCol>
                        <Ch.PbLabel>
                          {t('subscriptions.checkout.proratedCreditLine', {
                            plan: fromPlanName,
                          })}
                        </Ch.PbLabel>
                        {previousPlanLineSubtext ? (
                          <Ch.PbSubtext>{previousPlanLineSubtext}</Ch.PbSubtext>
                        ) : null}
                      </Ch.PbLabelCol>
                      <Ch.PbValCredit>
                        <CheckoutMoney ready={pricesReady}>
                          {proration
                            ? formatCreditAmount(proration.creditAmount)
                            : null}
                        </CheckoutMoney>
                      </Ch.PbValCredit>
                    </Ch.PbRow>
                  ) : null}
                  {showRemainingCredit ? (
                    <Ch.PbRow>
                      <Ch.PbLabel>
                        {t('subscriptions.checkout.remainingCreditLine')}
                      </Ch.PbLabel>
                      <Ch.PbValCredit>
                        <CheckoutMoney ready={pricesReady}>
                          {proration
                            ? formatRemainingCreditAmount(
                                proration.remainingCreditBalance,
                              )
                            : null}
                        </CheckoutMoney>
                      </Ch.PbValCredit>
                    </Ch.PbRow>
                  ) : null}
                  {tax > 0 ? (
                    <Ch.PbRow>
                      <Ch.PbLabel>{t('subscriptions.tax')}</Ch.PbLabel>
                      <Ch.PbVal>{tax}%</Ch.PbVal>
                    </Ch.PbRow>
                  ) : null}
                  {frequency === SubscriptionFrequency.YEAR &&
                  !isUpgradeCheckout &&
                  yearlySaveVsMonthly > 0 ? (
                    <Ch.PbRow $highlight>
                      <Ch.PbLabel>
                        {t('subscriptions.checkout.annualSavingLine')}
                      </Ch.PbLabel>
                      <Ch.PbVal>{t('changePlan.saveBadge')}</Ch.PbVal>
                    </Ch.PbRow>
                  ) : null}
                  <Ch.PbDivider />
                  <Ch.PbRowTotal>
                    <Ch.PbLabel>
                      {t('subscriptions.checkout.totalDueToday')}
                    </Ch.PbLabel>
                    <Ch.PbVal>
                      <CheckoutMoney ready={pricesReady} skeletonWidth="5.5rem">
                        {currencySym}
                        {formatCheckoutAmount(dueToday)}
                      </CheckoutMoney>
                    </Ch.PbVal>
                  </Ch.PbRowTotal>
                </Ch.PriceBreakdown>

                <Ch.IncludedSection>
                  <Ch.IncludedTitle>
                    {t('subscriptions.checkout.includedTitle')}
                  </Ch.IncludedTitle>
                  <Ch.IncludedList>
                    {includedItems.map((line, idx) => (
                      <Ch.IncludedItem key={`included-${idx}`}>
                        <Ch.IncludedCheck aria-hidden>✓</Ch.IncludedCheck>
                        {line}
                      </Ch.IncludedItem>
                    ))}
                  </Ch.IncludedList>
                </Ch.IncludedSection>

                <Ch.InfoBox>
                  <span aria-hidden>ℹ</span>
                  <span>
                    {isUpgradeCheckout
                      ? planChangeDirection === 'downgrade'
                        ? t('subscriptions.checkout.activationInfoDowngrade', {
                            plan: fromPlanName,
                          })
                        : t('subscriptions.checkout.activationInfoUpgrade', {
                            plan: fromPlanName,
                          })
                      : frequency === SubscriptionFrequency.YEAR
                      ? t('subscriptions.checkout.activationInfoStripe')
                      : t('subscriptions.checkout.activationInfoStripeMonthly')}
                  </span>
                </Ch.InfoBox>
                <Ch.Guarantee>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>🔒</Ch.GuaranteeIcon>
                    {t('subscriptions.checkout.trustSecureStripe')}
                  </Ch.GuaranteeItem>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>✕</Ch.GuaranteeIcon>
                    {t('subscriptions.checkout.trustCancel')}
                  </Ch.GuaranteeItem>
                </Ch.Guarantee>
              </Ch.OrderCard>
            </div>

            <Ch.PaymentCard>
              <Ch.PaymentCardTitle>
                {t('subscriptions.checkout.completeOrder')}
              </Ch.PaymentCardTitle>

              <Ch.PaymentMethodSection
                role="radiogroup"
                aria-label={t('subscriptions.checkout.paymentMethodLegend')}
              >
                <Ch.PaymentMethodLegend>
                  {t('subscriptions.checkout.paymentMethodLegend')}
                </Ch.PaymentMethodLegend>
                <Ch.PaymentMethodGrid>
                  <Ch.PaymentMethodCard
                    type="button"
                    $selected={paymentMethod === 'direct'}
                    onClick={() => {
                      setPaymentMethod('direct');
                      setDirectCheckoutError(null);
                    }}
                    aria-checked={paymentMethod === 'direct'}
                    role="radio"
                  >
                    <Ch.PaymentMethodCardTop>
                      <Ch.PaymentMethodRadioUi
                        $checked={paymentMethod === 'direct'}
                      />
                      <Ch.PaymentMethodText>
                        <Ch.PaymentMethodName>
                          {t('subscriptions.checkout.payDirectTitle')}
                        </Ch.PaymentMethodName>
                        <Ch.PaymentMethodDesc>
                          {t('subscriptions.checkout.payDirectDesc')}
                        </Ch.PaymentMethodDesc>
                      </Ch.PaymentMethodText>
                    </Ch.PaymentMethodCardTop>
                    <Ch.PaymentBrandStrip>
                      <Ch.PaymentBrandRow>
                        <VisaIcon />
                        <MastercardIcon />
                        <SepaIcon />
                        <StripeIcon />
                      </Ch.PaymentBrandRow>
                    </Ch.PaymentBrandStrip>
                  </Ch.PaymentMethodCard>
                  <Ch.PaymentMethodCard
                    type="button"
                    $selected={paymentMethod === 'invoice'}
                    onClick={() => {
                      setPaymentMethod('invoice');
                      setDirectCheckoutError(null);
                    }}
                    aria-checked={paymentMethod === 'invoice'}
                    role="radio"
                  >
                    <Ch.PaymentMethodCardTop>
                      <Ch.PaymentMethodRadioUi
                        $checked={paymentMethod === 'invoice'}
                      />
                      <Ch.PaymentMethodText>
                        <Ch.PaymentMethodName>
                          {t('subscriptions.checkout.payInvoiceTitle')}
                        </Ch.PaymentMethodName>
                        <Ch.PaymentMethodDesc>
                          {t('subscriptions.checkout.payInvoiceDesc')}
                        </Ch.PaymentMethodDesc>
                      </Ch.PaymentMethodText>
                    </Ch.PaymentMethodCardTop>
                    <Ch.InvoiceOptionStrip>
                      <InvoiceDocIcon />
                    </Ch.InvoiceOptionStrip>
                  </Ch.PaymentMethodCard>
                </Ch.PaymentMethodGrid>
              </Ch.PaymentMethodSection>

              {paymentMethod === 'direct' ? (
                <>
                  <Ch.PriceDisplay>
                    <Ch.PriceBig>
                      <Ch.PriceBigSup>{currencySym}</Ch.PriceBigSup>
                      <CheckoutMoney
                        ready={pricesReady}
                        skeletonWidth="6rem"
                        skeletonHeight={36}
                      >
                        {formatCheckoutAmount(dueToday)}
                      </CheckoutMoney>
                      <Ch.PricePeriod>
                        {isUpgradeCheckout
                          ? t('subscriptions.checkout.pricePeriodToday')
                          : frequency === SubscriptionFrequency.YEAR
                          ? t('changePlan.perYear')
                          : t('changePlan.perMonth')}
                      </Ch.PricePeriod>
                    </Ch.PriceBig>
                    {approxMonthlyEq != null && !isUpgradeCheckout ? (
                      <Ch.PriceSub>
                        {t('changePlan.approxPerMonth', {
                          price: `${currencySym}${formatCheckoutAmount(
                            approxMonthlyEq,
                          )}`,
                        })}
                      </Ch.PriceSub>
                    ) : isUpgradeCheckout ? (
                      <Ch.PriceSub>
                        {displayPlanName} · {maxCommunities}{' '}
                        {t('changePlan.specCommunities')} ·{' '}
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('subscriptions.yearly')
                          : t('subscriptions.monthly')}
                      </Ch.PriceSub>
                    ) : null}
                    {showProrationCredit ? (
                      <Ch.PriceSave>
                        {t('subscriptions.checkout.prorationAppliedBadge')}
                      </Ch.PriceSave>
                    ) : isUpgradeCheckout &&
                      frequency === SubscriptionFrequency.YEAR &&
                      yearlySaveVsMonthly > 0 ? (
                      <Ch.PriceSave>
                        {t('subscriptions.checkout.priceSaveVsMonthly')}
                      </Ch.PriceSave>
                    ) : null}
                    <Ch.PriceToday>
                      <strong>
                        {t('subscriptions.checkout.chargedToday')}{' '}
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {formatCheckoutAmount(dueToday)}
                        </CheckoutMoney>
                      </strong>
                      <br />
                      {isUpgradeCheckout &&
                      frequency === SubscriptionFrequency.YEAR
                        ? t('subscriptions.checkout.fullPriceFromDate', {
                            date: renewalDateStr,
                          })
                        : t('subscriptions.checkout.renewsOn', {
                            date: renewalDateStr,
                          })}
                    </Ch.PriceToday>
                  </Ch.PriceDisplay>

                  <Ch.PaySummaryRows>
                    <Ch.PaySummaryRow>
                      <Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryLabel>
                          {frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.checkout.planLineAnnual', {
                                plan: displayPlanName,
                              })
                            : t('subscriptions.checkout.planLineMonthly', {
                                plan: displayPlanName,
                              })}
                        </Ch.PaySummaryLabel>
                        {planLineSubtext ? (
                          <Ch.PaySummarySubtext>
                            {planLineSubtext}
                          </Ch.PaySummarySubtext>
                        ) : null}
                      </Ch.PaySummaryLabelCol>
                      <Ch.PaySummaryVal>
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {displayedLinePrice}
                        </CheckoutMoney>
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryRow>
                    {showProrationCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabelCol>
                          <Ch.PaySummaryLabel>
                            {t('subscriptions.checkout.proratedCreditSummary', {
                              plan: fromPlanName,
                            })}
                          </Ch.PaySummaryLabel>
                          {previousPlanLineSubtext ? (
                            <Ch.PaySummarySubtext>
                              {previousPlanLineSubtext}
                            </Ch.PaySummarySubtext>
                          ) : null}
                        </Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatCreditAmount(proration.creditAmount)
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    {showRemainingCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabel>
                          {t('subscriptions.checkout.remainingCreditSummary')}
                        </Ch.PaySummaryLabel>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatRemainingCreditAmount(
                                  proration.remainingCreditBalance,
                                )
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    <Ch.PaySummaryTotalRow>
                      <Ch.PaySummaryLabel>
                        {isUpgradeCheckout
                          ? t('subscriptions.checkout.totalDueToday')
                          : t('subscriptions.total')}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {formatCheckoutAmount(dueToday)}
                        </CheckoutMoney>
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryTotalRow>
                  </Ch.PaySummaryRows>

                  {payActionsBlock}

                  <Ch.StripeNote>
                    <span>
                      {t('subscriptions.checkout.stripeProcessedNote')}
                    </span>
                    <StripeWordmark height={16} />
                  </Ch.StripeNote>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>✕</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustCancelDetailed')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🔒</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustSSL')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </>
              ) : (
                <>
                  <Ch.InvoicePriceDisplay>
                    <Ch.InvoicePriceTitle>
                      {t('subscriptions.checkout.invoiceHeadline')}
                    </Ch.InvoicePriceTitle>
                    <Ch.InvoicePriceSub>
                      {t('subscriptions.checkout.invoiceSubline')}
                    </Ch.InvoicePriceSub>
                    {frequency !== SubscriptionFrequency.YEAR ? (
                      <Ch.InvoiceYearlyOnlyBanner role="status">
                        {t('subscriptions.checkout.invoiceYearlyOnly')}
                      </Ch.InvoiceYearlyOnlyBanner>
                    ) : null}
                  </Ch.InvoicePriceDisplay>

                  <Ch.PaySummaryRows>
                    <Ch.PaySummaryRow>
                      <Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryLabel>
                          {frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.checkout.planLineAnnual', {
                                plan: displayPlanName,
                              })
                            : t('subscriptions.checkout.planLineMonthly', {
                                plan: displayPlanName,
                              })}
                        </Ch.PaySummaryLabel>
                        {planLineSubtext ? (
                          <Ch.PaySummarySubtext>
                            {planLineSubtext}
                          </Ch.PaySummarySubtext>
                        ) : null}
                      </Ch.PaySummaryLabelCol>
                      <Ch.PaySummaryVal>
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {displayedLinePrice}
                        </CheckoutMoney>
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryRow>
                    {showProrationCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabelCol>
                          <Ch.PaySummaryLabel>
                            {t('subscriptions.checkout.proratedCreditSummary', {
                              plan: fromPlanName,
                            })}
                          </Ch.PaySummaryLabel>
                          {previousPlanLineSubtext ? (
                            <Ch.PaySummarySubtext>
                              {previousPlanLineSubtext}
                            </Ch.PaySummarySubtext>
                          ) : null}
                        </Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatCreditAmount(proration.creditAmount)
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    {showRemainingCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabel>
                          {t('subscriptions.checkout.remainingCreditSummary')}
                        </Ch.PaySummaryLabel>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatRemainingCreditAmount(
                                  proration.remainingCreditBalance,
                                )
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    <Ch.PaySummaryTotalRow>
                      <Ch.PaySummaryLabel>
                        {t('subscriptions.checkout.totalDueToday')}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        {isUpgradeCheckout ? (
                          <CheckoutMoney ready={pricesReady}>
                            {currencySym}
                            {formatCheckoutAmount(dueToday)}
                          </CheckoutMoney>
                        ) : (
                          t('subscriptions.checkout.toBeInvoiced')
                        )}
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryTotalRow>
                  </Ch.PaySummaryRows>

                  {showInvoiceForm ? (
                    <Ch.InvoiceForm
                      onSubmit={submitInvoiceSubscription}
                      noValidate
                    >
                      <Ch.InvoiceFieldLabel htmlFor="payer-email-0">
                        {t('subscriptions.checkout.invoicePayerEmailsLabel')}
                      </Ch.InvoiceFieldLabel>
                      <Ch.InvoiceHint>
                        {t('subscriptions.checkout.invoicePayerEmailsHint')}
                      </Ch.InvoiceHint>
                      {profile?.email ? (
                        <Ch.InvoiceHint>
                          {t('subscriptions.checkout.invoiceBillToProfile', {
                            email: profile.email,
                          })}
                        </Ch.InvoiceHint>
                      ) : null}
                      <Ch.InvoiceEmailList>
                        {payerEmailRows.map((row, i) => (
                          <Ch.InvoiceEmailRow key={`payer-email-${i}`}>
                            <Ch.InvoiceTextInput
                              id={i === 0 ? 'payer-email-0' : undefined}
                              type="email"
                              inputMode="email"
                              autoComplete="email"
                              name={`payerEmails[${i}]`}
                              value={row}
                              onChange={(e) =>
                                updatePayerRow(i, e.target.value)
                              }
                              placeholder={t(
                                'subscriptions.checkout.invoiceEmailPlaceholder',
                              )}
                              aria-invalid={Boolean(invoiceError)}
                            />
                            {payerEmailRows.length > 1 ? (
                              <Ch.BtnInvoiceRowRemove
                                type="button"
                                onClick={() => removePayerRow(i)}
                                aria-label={t(
                                  'subscriptions.checkout.invoiceRemoveEmail',
                                )}
                              >
                                ×
                              </Ch.BtnInvoiceRowRemove>
                            ) : null}
                          </Ch.InvoiceEmailRow>
                        ))}
                      </Ch.InvoiceEmailList>
                      <Ch.BtnInvoiceAddEmail
                        type="button"
                        onClick={addPayerRow}
                      >
                        {t('subscriptions.checkout.invoiceAddEmail')}
                      </Ch.BtnInvoiceAddEmail>
                      {invoiceError ? (
                        <Ch.InvoiceFormError role="alert">
                          {invoiceError}
                        </Ch.InvoiceFormError>
                      ) : null}
                      <Ch.BtnPurchase
                        type="submit"
                        disabled={invoiceSubmitting}
                      >
                        <span aria-hidden>📋</span>
                        {invoiceSubmitting
                          ? t('subscriptions.checkout.invoiceSubmitting')
                          : t('subscriptions.checkout.invoiceSubmit')}
                      </Ch.BtnPurchase>
                    </Ch.InvoiceForm>
                  ) : null}

                  <Ch.StripeNote as="div">
                    {t('subscriptions.checkout.invoicePageNote')}
                  </Ch.StripeNote>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>📧</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.invoiceTrust1')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🏦</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.invoiceTrust2')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </>
              )}
            </Ch.PaymentCard>
          </CheckoutGridComponent>
        </CheckoutPageWrapComponent>
      </Ch.CheckoutBackdrop>
      <CommunitySlotsModal
        isOpen={communitySlotsOpen}
        onClose={onCommunitySlotsClose}
        maxSlots={Math.max(0, maxCommunities ?? 0)}
        userId={profile?.userId ?? ''}
        communities={ownedCommunitiesList}
        savedSelection={pendingSlotSelection}
        onSaved={onCommunitySlotsSavedCheckout}
      />
    </OutsideAppPageContentWrapper>
  );
};

export default SubscriptionInner;
